import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { consumePostLoginRedirect } from "../../../utils/postLoginRedirect";
import { useAuth, apiFetch } from "../../../context/AuthContext";
import { clearProfileCache, primeProfileCache } from "../../../hooks/useProfile";
import { writeProfileToStorage } from "../../../utils/authStorage";
import { consumeLogoutNotice } from "../../../utils/authErrors";
import { sessionManager } from "../../../utils/sessionManager";
import { setClientIconUrl, iconSrcFromSplashRecord } from "../../../utils/clientIcon";
import { prefetchProfilePicture } from "../../(All) Sidebar/hooks/useProfilePicture";
import { getSplashMediaKind, getGifDurationMs } from "../../../utils/splashMedia";
import { resolveMediaSrc } from "../../../utils/resolveMediaSrc";

// How long the splash stays up when the media's own length can't be read,
// and the cap for very long media.
const SPLASH_FALLBACK_MS = 6000;
const SPLASH_MAX_MS = 15000;

// ─── Splash URL Helpers ────────────────────────────────────────────────────────

const resolveSplashUrl = (record, baseUrl) => {
  if (!record) return null;
  const source = record.fullUrl || record.gifUrl;
  if (!source) return null;
  const trimmed = source.trim();
  if (!trimmed) return null;
  if (
    trimmed.toLowerCase() === "null" ||
    trimmed.toLowerCase() === "undefined"
  ) {
    return null;
  }
  if (/^https?:\/\//i.test(source)) return source;
  // A relative value here is our own local-dev streaming route
  // (/api/client-splash/:clientName/preview/gif) — needs the API origin and
  // the user's auth token attached; resolveMediaSrc() does both. Signed GCS
  // URLs never reach this branch (they're already absolute https:// links).
  const resolved = resolveMediaSrc(source.startsWith("/") ? source : `/${source}`);
  if (resolved && resolved !== source) return resolved;
  if (!baseUrl) return source.startsWith("/") ? source : `/${source}`;
  const normalizedBase = baseUrl.replace(/\/+$/, "");
  const normalizedPath = source.startsWith("/") ? source : `/${source}`;
  return `${normalizedBase}${normalizedPath}`;
};

const buildSplashUrlCandidates = (resolvedUrl, record, baseUrl) => {
  const candidates = [];
  const pushUnique = (value) => {
    if (!value) return;
    const trimmed = value.trim();
    if (!trimmed) return;
    if (
      trimmed.toLowerCase() === "null" ||
      trimmed.toLowerCase() === "undefined"
    )
      return;
    if (!candidates.includes(trimmed)) candidates.push(trimmed);
  };

  pushUnique(resolvedUrl);

  if (record?.gifUrl) {
    const relativePath = record.gifUrl.startsWith("/")
      ? record.gifUrl
      : `/${record.gifUrl}`;
    if (baseUrl) {
      const normalizedBase = baseUrl.replace(/\/+$/, "");
      pushUnique(`${normalizedBase}${relativePath}`);
    }
    pushUnique(
      `${window.location.origin.replace(/\/$/, "")}${relativePath}`
    );
  }

  if (resolvedUrl) {
    try {
      const parsed = new URL(resolvedUrl, window.location.origin);
      const pageProtocol = window.location.protocol;
      if (parsed.protocol === "https:" && pageProtocol === "http:") {
        const httpVariant = new URL(parsed.toString());
        httpVariant.protocol = "http:";
        pushUnique(httpVariant.toString());

        const httpAlt = new URL(httpVariant.toString());
        if (!httpAlt.port && parsed.port) {
          httpAlt.port = parsed.port;
          pushUnique(httpAlt.toString());
        }

        if (httpAlt.port === "3000") {
          const httpPortVariant = new URL(httpAlt.toString());
          httpPortVariant.port = "3002";
          pushUnique(httpPortVariant.toString());
        }
      }
    } catch (error) {
      console.debug("Unable to build HTTP fallback for splash URL", error);
    }
  }

  return candidates;
};

// ─── Hook ──────────────────────────────────────────────────────────────────────

const useLoginForm = (t) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [splashOverlay, setSplashOverlay] = useState({
    visible: false,
    url: null,
    loading: false,
  });

  // ── Lockout / throttle countdown (enterprise-style, à la SAP & Oracle) ──
  // When the server answers 429 (account temporarily locked, or IP rate
  // limited) we surface a LIVE countdown and keep the submit button disabled
  // until it reaches zero — the user always knows exactly when they can
  // retry, instead of blindly hammering a rejecting endpoint.
  const [lockoutSecondsLeft, setLockoutSecondsLeft] = useState(0);
  const [isAccountLocked, setIsAccountLocked] = useState(false);
  const lockoutIntervalRef = useRef(null);
  // Which identifier the ACCOUNT lockout belongs to. An account lockout for
  // user A must not block signing in as user B from the same form — only
  // submissions for the SAME identifier are frozen. IP-level throttling
  // (accountLocked=false) has no identifier and blocks the form regardless,
  // since the server would reject any identifier from this IP anyway.
  const lockedIdentifierRef = useRef(null);

  const clearLockoutTimer = () => {
    if (lockoutIntervalRef.current) {
      clearInterval(lockoutIntervalRef.current);
      lockoutIntervalRef.current = null;
    }
  };

  // Persisted so a page refresh or a new tab can't bypass the device-level
  // lock — the deadline (epoch ms) is shared via localStorage.
  const LOCKOUT_STORAGE_KEY = "loginLockout";

  const clearLockoutState = () => {
    clearLockoutTimer();
    lockedIdentifierRef.current = null;
    setIsAccountLocked(false);
    setLockoutSecondsLeft(0);
    try {
      localStorage.removeItem(LOCKOUT_STORAGE_KEY);
    } catch { /* storage unavailable — countdown still enforced in-memory */ }
  };

  const startLockoutCountdown = (seconds, accountLocked, identifier = null) => {
    clearLockoutTimer();
    lockedIdentifierRef.current = accountLocked ? identifier : null;
    setIsAccountLocked(!!accountLocked);
    setLockoutSecondsLeft(seconds);
    try {
      localStorage.setItem(
        LOCKOUT_STORAGE_KEY,
        JSON.stringify({
          until: Date.now() + seconds * 1000,
          accountLocked: !!accountLocked,
        })
      );
    } catch { /* storage unavailable — countdown still enforced in-memory */ }
    lockoutIntervalRef.current = setInterval(() => {
      setLockoutSecondsLeft((prev) => {
        if (prev <= 1) {
          clearLockoutTimer();
          lockedIdentifierRef.current = null;
          setIsAccountLocked(false);
          setError("");
          try {
            localStorage.removeItem(LOCKOUT_STORAGE_KEY);
          } catch { /* ignore */ }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Restore a still-running lockout after refresh / in a new tab.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LOCKOUT_STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      const secondsLeft = Math.ceil((saved.until - Date.now()) / 1000);
      if (secondsLeft > 0) {
        startLockoutCountdown(secondsLeft, saved.accountLocked);
        setError(
          saved.accountLocked
            ? (t.accountLocked || "Account temporarily locked due to too many failed attempts.")
            : (t.tooManyAttempts || "Too many attempts. Please wait before trying again.")
        );
      } else {
        localStorage.removeItem(LOCKOUT_STORAGE_KEY);
      }
    } catch { /* corrupt/unavailable storage — start unlocked */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The backend's lockout now escalates gradually (1m, 5m, 15m, 1h, 6h, 1d,
  // ... up to 30 days for a de-facto permanent lockdown — see userService.js
  // registerFailedLogin). Plain mm:ss stops being readable once a lockout
  // runs into hours or days, so scale the format to the remaining duration.
  const formatCountdown = (totalSeconds) => {
    const s = Math.max(0, Math.floor(totalSeconds));
    const days = Math.floor(s / 86400);
    const hours = Math.floor((s % 86400) / 3600);
    const minutes = Math.floor((s % 3600) / 60);
    const seconds = s % 60;

    if (days > 0) {
      return `${days}d ${String(hours).padStart(2, "0")}h ${String(minutes).padStart(2, "0")}m`;
    }
    if (hours > 0) {
      return `${hours}h ${String(minutes).padStart(2, "0")}m ${String(seconds).padStart(2, "0")}s`;
    }
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  };

  // Past this point a lockout is effectively "complete lockdown" — waiting
  // it out is impractical, so the UI should steer the user to Forgot
  // Password (which clears the lockout server-side) instead of just ticking
  // a countdown down from days.
  const LONG_LOCKOUT_THRESHOLD_SECONDS = 60 * 60; // 1 hour
  const isLongLockout =
    isAccountLocked && lockoutSecondsLeft > LONG_LOCKOUT_THRESHOLD_SECONDS;

  // ── Forgot-password resend cooldown ─────────────────────────────────────
  // After a reset link is sent, the "send" button stays disabled for 60s
  // (matching the server's per-account cooldown) with a visible countdown —
  // standard resend-throttling UX. Lives OUTSIDE the modal open/close reset
  // so closing and reopening the modal can't bypass it.
  const RESEND_COOLDOWN_SECONDS = 60;
  const RESET_COOLDOWN_STORAGE_KEY = "resetCooldown";
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState(0);
  const resendIntervalRef = useRef(null);

  // BROWSER-WIDE cooldown: once a reset email has been sent, no further
  // request is possible — for ANY email address — until it elapses. The
  // email field itself is frozen. Persisted to localStorage (refresh/new
  // tab) and enforced server-side by a signed cookie, so it can't be
  // bypassed client-side.
  const startResendCooldown = (seconds) => {
    if (resendIntervalRef.current) clearInterval(resendIntervalRef.current);
    setResendCooldownSeconds(seconds);
    try {
      localStorage.setItem(
        RESET_COOLDOWN_STORAGE_KEY,
        JSON.stringify({ until: Date.now() + seconds * 1000 })
      );
    } catch { /* storage unavailable — in-memory countdown still applies */ }
    resendIntervalRef.current = setInterval(() => {
      setResendCooldownSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(resendIntervalRef.current);
          resendIntervalRef.current = null;
          try {
            localStorage.removeItem(RESET_COOLDOWN_STORAGE_KEY);
          } catch { /* ignore */ }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Restore a still-running reset cooldown after refresh / in a new tab.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(RESET_COOLDOWN_STORAGE_KEY);
      if (!raw) return;
      const secondsLeft = Math.ceil(
        (JSON.parse(raw).until - Date.now()) / 1000
      );
      if (secondsLeft > 0) startResendCooldown(secondsLeft);
      else localStorage.removeItem(RESET_COOLDOWN_STORAGE_KEY);
    } catch { /* corrupt/unavailable storage — start without cooldown */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset password modal state
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetStatus, setResetStatus] = useState(null);
  const [resetMessage, setResetMessage] = useState("");
  const [isResetLoading, setIsResetLoading] = useState(false);

  // Forced password change — a phone-only account (no email on file) can't
  // be sent a reset link, so instead of blocking login entirely (like the
  // email first-login flow does), the server lets it log in once and flags
  // `mustChangePassword: true`. This modal blocks navigation into the app
  // until a new password is actually set.
  const [isForcePasswordChangeOpen, setIsForcePasswordChangeOpen] = useState(false);
  const [forcePasswordChangeError, setForcePasswordChangeError] = useState("");
  const [isForcePasswordChangeLoading, setIsForcePasswordChangeLoading] = useState(false);
  // Holds the login response so the rest of the post-login flow (profile
  // fetch, splash, navigation) can resume once the password change succeeds.
  const pendingLoginDataRef = useRef(null);

  const { login } = useAuth();
  const navigate = useNavigate();
  const splashTimerRef = useRef(null);
  const splashFailsafeTimerRef = useRef(null);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (splashTimerRef.current) clearTimeout(splashTimerRef.current);
      if (splashFailsafeTimerRef.current)
        clearTimeout(splashFailsafeTimerRef.current);
      if (lockoutIntervalRef.current) clearInterval(lockoutIntervalRef.current);
      if (resendIntervalRef.current) clearInterval(resendIntervalRef.current);
    };
  }, []);

  // Why the previous session ended, when the server ended it (token
  // rejected, user removed, account inactive). One-shot: read on mount.
  const [logoutNotice, setLogoutNotice] = useState(null);
  useEffect(() => {
    const reason = consumeLogoutNotice();
    if (reason) setLogoutNotice(reason);
  }, []);

  // ✅ If this tab is already authenticated when the login page mounts —
  // either because it was opened straight to /login while logged in, or
  // because another tab just logged in and sessionManager's login broadcast
  // reloaded this one — send it straight to the right dashboard instead of
  // leaving it sitting on the login form. Without this, logging in on tab A
  // never "reached" tab B: the shared token/role were already in
  // localStorage, but nothing told an already-open login tab to look.
  useEffect(() => {
    if (sessionManager.isAuthenticated()) {
      const role = sessionManager.getUserRole();
      const target = getNavigationTarget(role);
      if (target) {
        navigate(target, { replace: true });
      }
    }
    // Only ever check once, on mount — handleSubmit owns the post-login
    // navigation for an actual login performed in this tab.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Navigation Target Helper ──────────────────────────────────────────────

  const getNavigationTarget = (role) => {
    // A deep link saved by PrivateRoute before login (e.g. the "salary paid"
    // email button) wins — but only inside this role's own User-View shell.
    const saved = consumePostLoginRedirect(role);
    if (saved) {
      localStorage.setItem("viewType", "user");
      return saved;
    }
    switch (role) {
      case "admin":
        localStorage.setItem("viewType", "user");
        return "/userprofileadmin/professionalProfile";
      case "user":
        localStorage.setItem("viewType", "user");
        return "/userprofile";
      case "manager":
        localStorage.setItem("viewType", "user");
        return "/userprofilemanager/professionalProfile";
      case "superadmin":
        localStorage.setItem("viewType", "superadmin");
        return "/DashboardSuper/ConfigurationManagement";
      default:
        return null;
    }
  };

  // ─── Phone Number Normalization ────────────────────────────────────────────
  // The backend (userService.normalizePhone / employeeService.normalizePhoneForLogin)
  // never strips a leading "0" — it only strips non-digit characters — so
  // whatever was typed/imported for an employee's phone (almost always the
  // local "0"-prefixed form, e.g. "01011203008") is exactly what ends up
  // stored on User.phoneNumber. Stripping that leading 0 here used to make
  // typing the number EXACTLY as it appears in the system fail to match.
  // Normalize every input variant TOWARD that 0-prefixed local form instead
  // of away from it. Email identifiers are untouched.

  const normalizeEgyptPhone = (raw) => {
    const trimmed = raw.trim();
    const hasPlus = trimmed.startsWith("+");
    let digits = trimmed.replace(/\D/g, ""); // strip +, spaces, dashes, etc.

    if (hasPlus && digits.startsWith("20")) {
      // +201011203008 -> 01011203008
      digits = `0${digits.slice(2)}`;
    } else if (!hasPlus && digits.length === 12 && digits.startsWith("20")) {
      // 201011203008 (country code, no plus) -> 01011203008
      digits = `0${digits.slice(2)}`;
    } else if (!digits.startsWith("0") && digits.length === 10) {
      // Bare 10-digit, no leading 0 -> 1011203008 -> 01011203008
      digits = `0${digits}`;
    }
    // else already has the leading 0 — e.g. "01011203008" typed exactly as
    // stored — left completely untouched so it matches byte-for-byte.

    return digits;
  };

  // ─── Splash Logic ──────────────────────────────────────────────────────────

  const handleSplashNavigation = (activeSplash, clientName, target, BASE_URL) => {
    const splashUrl = resolveSplashUrl(activeSplash, BASE_URL);

    if (splashUrl) {
      const candidateUrls = buildSplashUrlCandidates(
        splashUrl,
        activeSplash,
        BASE_URL
      );

      if (candidateUrls.length > 0) {
        // A video URL can't be probed with `new Image()` — it errors
        // immediately since the browser can't decode video as an image,
        // which used to skip straight past every candidate and never show
        // the splash at all. Use a real <video> prober for video media and
        // fall back to Image() probing for gif/image media as before.
        // Detect kind from the RAW stored gifUrl, not the resolved display
        // URL — in local dev the resolved URL is our own extension-less
        // streaming route (/preview/gif), which getSplashMediaKind can't
        // read a file extension from.
        const mediaKind = getSplashMediaKind(activeSplash?.gifUrl || splashUrl);
        const isVideo = mediaKind === "video";
        const prober = isVideo
          ? document.createElement("video")
          : new window.Image();
        if (isVideo) {
          prober.muted = true;
          prober.playsInline = true;
          prober.preload = "auto";
        }
        let attemptIndex = 0;

        const finalizeNavigation = (delay) => {
          if (splashTimerRef.current) clearTimeout(splashTimerRef.current);
          splashTimerRef.current = setTimeout(() => {
            setSplashOverlay({ visible: false, url: null, loading: false });
            navigate(target);
            splashTimerRef.current = null;
          }, delay);
        };

        const cleanupFailsafe = () => {
          if (splashFailsafeTimerRef.current) {
            clearTimeout(splashFailsafeTimerRef.current);
            splashFailsafeTimerRef.current = null;
          }
        };

        const startFailsafe = () => {
          if (!splashFailsafeTimerRef.current) {
            splashFailsafeTimerRef.current = setTimeout(() => {
              setSplashOverlay((prev) =>
                prev.visible ? { ...prev, loading: false } : prev
              );
              navigate(target);
              splashFailsafeTimerRef.current = null;
            }, 7000);
          }
        };

        const tryLoad = (index) => {
          const nextUrl = candidateUrls[index];
          if (!nextUrl) {
            cleanupFailsafe();
            finalizeNavigation(500);
            return;
          }
          setSplashOverlay({ visible: true, url: nextUrl, loading: true, mediaKind });
          prober.src = nextUrl;
          if (isVideo) prober.load();
        };

        const handleReady = () => {
          cleanupFailsafe();
          setSplashOverlay({
            visible: true,
            url: candidateUrls[attemptIndex],
            loading: false,
            mediaKind,
          });
          // Keep the splash up for exactly ONE play of the media — a fixed
          // 6s let a short looping gif play two or three times.
          if (isVideo) {
            const ms = Number.isFinite(prober.duration) ? prober.duration * 1000 : 0;
            finalizeNavigation(ms > 0 ? Math.min(ms, SPLASH_MAX_MS) : SPLASH_FALLBACK_MS);
            return;
          }
          const readyAt = Date.now();
          getGifDurationMs(candidateUrls[attemptIndex]).then((ms) => {
            const duration = ms ? Math.min(ms, SPLASH_MAX_MS) : SPLASH_FALLBACK_MS;
            finalizeNavigation(Math.max(0, duration - (Date.now() - readyAt)));
          });
        };

        const handleFailure = () => {
          attemptIndex += 1;
          if (attemptIndex < candidateUrls.length) {
            tryLoad(attemptIndex);
            return;
          }
          cleanupFailsafe();
          setSplashOverlay({ visible: true, url: null, loading: true, mediaKind });
          finalizeNavigation(800);
        };

        if (isVideo) {
          // Not `{ once: true }` — tryLoad() reuses this same element across
          // candidate URLs (reassigning .src + .load()), so the listener
          // must survive more than one attempt.
          prober.addEventListener("loadeddata", handleReady);
          prober.addEventListener("error", handleFailure);
        } else {
          prober.onload = handleReady;
          prober.onerror = handleFailure;
        }

        startFailsafe();
        tryLoad(attemptIndex);
        return;
      }
    }

    // No splash — navigate directly
    setSplashOverlay({ visible: false, url: null, loading: false });
    splashTimerRef.current = setTimeout(() => {
      navigate(target);
      splashTimerRef.current = null;
    }, 500);
    if (splashFailsafeTimerRef.current) {
      clearTimeout(splashFailsafeTimerRef.current);
      splashFailsafeTimerRef.current = null;
    }
  };

  // ─── Post-authentication continuation ──────────────────────────────────────
  // Runs the rest of a successful login (profile fetch, splash, navigation)
  // — called either immediately after /users/login, or after a forced
  // password change completes for a phone-only first login.
  const completeLoginFlow = async (data) => {
    const BASE_URL = process.env.REACT_APP_BASE_URL;

    // No-op when this runs after the forced password-change modal — the
    // token was already stashed to localStorage the moment mustChangePassword
    // came back, so the authenticated change-password call could use it.
    localStorage.setItem("token", data.token);
    clearProfileCache();

    // Fetch profile
    let profile = null;
    let role = null;
    let clientName = null;

    try {
      const profileRes = await apiFetch(`${BASE_URL}/users/me`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${data.token}`,
        },
      });

      if (profileRes.ok) {
        const profileJson = await profileRes.json();
        if (profileJson.success && profileJson.profile) {
          profile = profileJson.profile;
          // Exact mirror of this account's profile — the previous account's
          // fields must not survive where this one has no value.
          writeProfileToStorage(profile);
          // The server just confirmed who this is: seed the shared identity
          // store so the shell renders it without asking again.
          primeProfileCache(profile);
          localStorage.setItem("email", email);
          if (profile.role) role = profile.role;
          if (profile.clientName) clientName = profile.clientName;

          // Warm the profile picture cache so the topbar avatar is
          // ready the moment the dashboard renders (fire-and-forget)
          if (profile.infinityCode) {
            prefetchProfilePicture(profile.infinityCode);
          }
        }
      }
    } catch (profileErr) {
      console.warn("Failed to fetch user profile after login", profileErr);
    }

    const target = getNavigationTarget(role);
    if (!target) {
      setError(t.noPermission);
      setIsLoading(false);
      return;
    }

    setSuccess(t.loginSuccessful);
    window.dispatchEvent(new Event("managerViewTypeChanged"));

    // Fetch splash if not in login response
    let splashRecord = data?.splashScreen || null;
    if (!splashRecord && clientName) {
      try {
        const splashRes = await apiFetch(
          `${BASE_URL}/api/client-splash/${encodeURIComponent(clientName)}`
        );
        if (splashRes.ok) {
          const splashJson = await splashRes.json();
          if (splashJson?.success && splashJson?.data) {
            splashRecord = splashJson.data;
          }
        }
      } catch (splashError) {
        console.warn("Failed to pull splash screen after login", splashError);
      }
    }

    // isActive only gates the splash animation — the badge icon on the same
    // record shows either way.
    const activeSplash =
      splashRecord && splashRecord.isActive !== false
        ? splashRecord
        : null;

    const clientIconUrl = iconSrcFromSplashRecord(splashRecord);
    setClientIconUrl(clientIconUrl);

    login(email, rememberMe, {
      email: profile?.employeeEmail || email,
      role,
      workingCompany: profile?.workingCompany,
      clientIconUrl,
      token: data.token,
      adminLevel: profile?.adminLevel || null,
    });

    handleSplashNavigation(activeSplash, clientName, target, BASE_URL);
  };

  // ─── Forced Password Change Handlers ───────────────────────────────────────

  const handleForcePasswordChangeSubmit = async (newPassword) => {
    setIsForcePasswordChangeLoading(true);
    setForcePasswordChangeError("");
    try {
      const BASE_URL = process.env.REACT_APP_BASE_URL;
      const data = pendingLoginDataRef.current;
      const token = data?.token || localStorage.getItem("token");

      const res = await apiFetch(`${BASE_URL}/users/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        // `password` is the one they just used to log in (the provisioned
        // default) — no need to make them retype it as "old password".
        body: JSON.stringify({ oldPassword: password, newPassword }),
      });
      const result = await res.json().catch(() => ({}));

      if (!res.ok || !result.success) {
        throw new Error(result.error || t.networkError);
      }

      setIsForcePasswordChangeOpen(false);
      setIsLoading(true);
      await completeLoginFlow(data);
    } catch (err) {
      setForcePasswordChangeError(err.message || t.networkError);
    } finally {
      setIsForcePasswordChangeLoading(false);
    }
  };

  // Bails out of the forced password change instead of completing it — drops
  // the token that was stashed for the change-password call so the tab goes
  // back to a clean, logged-out login form rather than a half-authenticated
  // state.
  const handleForcePasswordChangeLogoutInstead = () => {
    localStorage.removeItem("token");
    pendingLoginDataRef.current = null;
    setIsForcePasswordChangeOpen(false);
    setForcePasswordChangeError("");
    setPassword("");
  };

  // ─── Submit Handler ────────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLogoutNotice(null);
    setSplashOverlay({ visible: false, url: null, loading: false });

    if (splashTimerRef.current) {
      clearTimeout(splashTimerRef.current);
      splashTimerRef.current = null;
    }
    if (splashFailsafeTimerRef.current) {
      clearTimeout(splashFailsafeTimerRef.current);
      splashFailsafeTimerRef.current = null;
    }

    // `email` is really "email or phone number" now — only lowercase it when
    // it actually looks like an email (lowercasing a phone number is a
    // no-op for digits anyway, but avoid mangling a leading "+").
    const trimmedIdentifier = email.trim();
    const isEmailLike = trimmedIdentifier.includes("@");
    const normalizedIdentifier = isEmailLike
      ? trimmedIdentifier.toLowerCase()
      : normalizeEgyptPhone(trimmedIdentifier);

    if (!normalizedIdentifier || !password) {
      setError(t.fillAllFields);
      return;
    }

    // Hard stop while ANY throttle/lockout countdown is running — a lockout
    // on one account freezes this device's login form entirely until the
    // window elapses (deliberate policy: prevents an attacker from pivoting
    // to other accounts from the same device after triggering a lockout).
    if (lockoutSecondsLeft > 0) {
      return;
    }

    // NOTE: this used to short-circuit here by calling
    // sessionManager.getActiveSession() and, if ANY session was active in
    // ANY tab (any user), logging this tab in as that session — completely
    // skipping password verification and ignoring the email/password just
    // typed. That's what produced "weird" data when two tabs were open at
    // once: submitting the login form in tab 2 could silently log it in as
    // whoever's session was active in tab 1. Real authentication must
    // always go through the API below.

    setIsLoading(true);

    // Location is best-effort telemetry only — it must NEVER block login.
    // (The old flow wrapped the whole login call in getCurrentPosition, so a
    // denied/blocked/timed-out location prompt failed every sign-in before it
    // even reached the server.)
    const performLogin = async (position = null) => {
      const BASE_URL = process.env.REACT_APP_BASE_URL;

      try {
        const res = await apiFetch(`${BASE_URL}/users/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            identifier: normalizedIdentifier,
            password,
            ...(position?.coords
              ? {
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
              }
              : {}),
          }),
        });

        const data = await res.json();

        if (res.ok) {
          // Any countdown still on screen (e.g. from a different account's
          // lockout) is now irrelevant for this successful attempt.
          clearLockoutState();

          if (data.firstLogin) {
            setError(data.message || t.firstLoginDetected);
            setIsLoading(false);
            return;
          }

          // Phone-only account, first login: server let it through but
          // flags that the (shared/default) password must be changed
          // before the user gets into the app. Stash the token now (the
          // change-password call needs it) and pause here — the rest of
          // this success path (profile fetch, splash, navigation) resumes
          // in completeLoginFlow() once the modal reports success.
          if (data.mustChangePassword) {
            localStorage.setItem("token", data.token);
            pendingLoginDataRef.current = data;
            setForcePasswordChangeError("");
            setIsForcePasswordChangeOpen(true);
            setIsLoading(false);
            return;
          }

          await completeLoginFlow(data);
        } else {
          if (res.status === 429) {
            // Account lockout or rate limit — start a visible countdown and
            // freeze the form until it elapses.
            const retryAfter =
              Number(data.retryAfterSeconds) ||
              Number(res.headers.get("Retry-After")) ||
              900;
            startLockoutCountdown(retryAfter, !!data.accountLocked, normalizedIdentifier);
            setError(
              data.accountLocked
                ? (t.accountLocked || "Account temporarily locked due to too many failed attempts.")
                : (t.tooManyAttempts || "Too many attempts. Please wait before trying again.")
            );
          } else if (data.subscriptionError) {
            switch (data.errorType) {
              case "not_found":
                setError(t.subscriptionNotFound);
                break;
              case "inactive":
                setError(t.subscriptionInactive);
                break;
              case "expired":
                setError(data.error || t.subscriptionExpired);
                break;
              default:
                setError(data.error || t.subscriptionValidationFailed);
            }
          } else if (res.status === 403 && data.supportRequired) {
            setError(data.error || t.contactSupportToSetPassword);
          } else if (res.status === 401) {
            // Wrong email/password — show the translated message, not the
            // server's raw "Authentication failed"
            setError(t.invalidCredentials);
          } else {
            setError(data.error || t.invalidCredentials);
          }
        }
      } catch (err) {
        console.error("Login error:", err);
        setError(t.networkError);
      } finally {
        setIsLoading(false);
      }
    };

    // Fire login immediately; attach coordinates only if the browser answers
    // fast. Never wait on (or fail because of) the location prompt.
    if (navigator.geolocation) {
      let started = false;
      const startOnce = (position) => {
        if (!started) { started = true; performLogin(position); }
      };
      const timer = setTimeout(() => startOnce(null), 1500);
      navigator.geolocation.getCurrentPosition(
        (position) => { clearTimeout(timer); startOnce(position); },
        () => { clearTimeout(timer); startOnce(null); },
        { timeout: 1200, maximumAge: 600000 }
      );
    } else {
      performLogin(null);
    }
  };

  // ─── Reset Password Handlers ───────────────────────────────────────────────

  const handleOpenResetModal = (e) => {
    e.preventDefault();
    setIsResetModalOpen(true);
  };

  const handleCloseResetModal = () => {
    setResetEmail("");
    setResetStatus(null);
    setResetMessage("");
    setIsResetLoading(false);
    setIsResetModalOpen(false);
  };

  const handleResetPasswordSubmit = async () => {
    const normalizedResetEmail = resetEmail.toLowerCase().trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(normalizedResetEmail)) {
      setResetStatus("error");
      setResetMessage(t.validEmailRequired);
      return;
    }

    // Browser-wide cooldown running — no request goes out, for any email.
    if (resendCooldownSeconds > 0) {
      setResetStatus("error");
      setResetMessage(t.resendCooldownActive || "Please wait before requesting another reset link.");
      return;
    }

    setIsResetLoading(true);
    setResetStatus(null);
    setResetMessage("");

    try {
      const BASE_URL = process.env.REACT_APP_BASE_URL;
      const response = await apiFetch(`${BASE_URL}/users/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: normalizedResetEmail }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setResetStatus("success");
        setResetMessage(t.resetInstructionsSent);
        // Start the visible 60s resend cooldown (mirrors the server-side
        // per-account cooldown so the button re-enables when a resend
        // would actually go through).
        startResendCooldown(RESEND_COOLDOWN_SECONDS);

        if (process.env.NODE_ENV === "development" && data.resetToken) {
          console.log("🔑 Development Reset Token:", data.resetToken);
        }

        setTimeout(handleCloseResetModal, 3000);
      } else if (response.status === 429) {
        // Server-side rate limit — honor its retry window in the countdown.
        const retryAfter =
          Number(data.retryAfterSeconds) ||
          Number(response.headers.get("Retry-After")) ||
          RESEND_COOLDOWN_SECONDS;
        startResendCooldown(retryAfter);
        setResetStatus("error");
        setResetMessage(t.resendCooldownActive || "Too many reset requests. Please wait before trying again.");
      } else {
        setResetStatus("error");
        setResetMessage(data.error || t.resetInstructionsFailed);
      }
    } catch (error) {
      console.error("Forgot password error:", error);
      setResetStatus("error");
      setResetMessage(t.networkErrorReset);
    } finally {
      setIsResetLoading(false);
    }
  };

  const handleResetKeyPress = (e) => {
    if (e.key === "Enter" && !isResetLoading && resetStatus !== "success") {
      handleResetPasswordSubmit();
    }
  };

  // Any running countdown — account lockout OR IP throttle — freezes the
  // whole form (device-level lock): switching to a different identifier
  // does NOT re-enable submission until the window elapses.
  const lockoutBlocksCurrent = lockoutSecondsLeft > 0;

  return {
    // Shown once after the server ended the session ("session_ended" /
    // "account_inactive"), cleared on the next sign-in attempt.
    logoutNotice,
    // Form state
    email, setEmail,
    password, setPassword,
    rememberMe, setRememberMe,
    error,
    success,
    isLoading,
    splashOverlay,
    // Lockout / throttle countdown
    lockoutSecondsLeft,
    isAccountLocked,
    lockoutBlocksCurrent,
    isLongLockout,
    formatCountdown,
    // Handlers
    handleSubmit,
    // Reset modal state
    isResetModalOpen,
    resetEmail, setResetEmail,
    resetStatus,
    resetMessage,
    isResetLoading,
    resendCooldownSeconds,
    // Reset modal handlers
    handleOpenResetModal,
    handleCloseResetModal,
    handleResetPasswordSubmit,
    handleResetKeyPress,
    // Forced password change modal (phone-only first login)
    isForcePasswordChangeOpen,
    forcePasswordChangeError,
    isForcePasswordChangeLoading,
    handleForcePasswordChangeSubmit,
    handleForcePasswordChangeLogoutInstead,
  };
};

export default useLoginForm;